import { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  Activity,
  RefreshCw,
  Layers,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  MessageSquare,
  BarChart3,
  Bot,
  User,
  Archive,
  ArchiveRestore,
  MoreVertical,
  Plus,
} from "lucide-react";
import {
  testRunsListTestRuns,
  testRunsGetTestRun,
  testRunsArchiveTestRun,
  testRunsUnarchiveTestRun,
  feedbackBatchesListFeedbackBatches,
  feedbackBatchesGetFeedbackBatch,
  commentsListComments,
  evaluationsListEvaluations,
  rulesListRules,
  knowledgeListKnowledgeItems,
} from "../../gen";
import type {
  TestRunResponse,
  TestRunListResponse,
  TestCaseResult,
  TestCaseTurnComparison,
  BatchResponse,
  CommentResponse,
  EvaluationResponse,
  RuleResponse,
  KnowledgeItemResponse,
} from "../../gen/models";
import { useOrg } from "../../hooks/useOrg";
import { useToast } from "../../context/ToastContext";
import { TimeAgo } from "../../components/common/TimeAgo";
import { HighlightedMessageText } from "../../components/thread/HighlightedMessageText";
import { InPlaceCommentComposer } from "../../components/thread/InPlaceCommentComposer";
import { ThreadCommentCard } from "../../components/thread/ThreadCommentCard";
import { EntityLinkedText } from "../../components/evaluation/EntityLinkedText";
import { useInPlaceComposer } from "../../components/thread/useInPlaceComposer";
import { useCommentEditor } from "../../components/thread/useCommentEditor";
import { extractListItems } from "../../components/thread/threadUtils";

export function TestRunsWorkspace() {
  const { run_id: pathRunId } = useParams<{ run_id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeOrgId } = useOrg();
  const { toast } = useToast();

  const queryRunId =
    searchParams.get("test_run_id") ||
    searchParams.get("run_id") ||
    searchParams.get("test-run");
  const targetRunId = queryRunId || pathRunId || null;

  const showArchivedParam =
    searchParams.get("include_archived") === "true" ||
    searchParams.get("archived") === "true";

  const [runs, setRuns] = useState<TestRunResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(showArchivedParam);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(
    targetRunId,
  );
  const [runDetail, setRunDetail] = useState<TestRunResponse | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsedCases, setCollapsedCases] = useState<Record<string, boolean>>(
    {},
  );

  const [comments, setComments] = useState<CommentResponse[]>([]);
  const [evaluations, setEvaluations] = useState<EvaluationResponse[]>([]);
  const [activeHighlightedCommentId, setActiveHighlightedCommentId] = useState<
    string | null
  >(null);
  const [rulesList, setRulesList] = useState<RuleResponse[]>([]);
  const [knowledgeList, setKnowledgeList] = useState<KnowledgeItemResponse[]>(
    [],
  );
  const [_allBatches, setAllBatches] = useState<BatchResponse[]>([]);
  const [defaultBatch, setDefaultBatch] = useState<BatchResponse | null>(null);

  const isOpenBatch = Boolean(
    defaultBatch && defaultBatch.status === "collecting",
  );

  const {
    inPlaceComposer,
    setInPlaceComposer,
    composerText,
    composerRuleIds,
    composerKnowledgeIds,
    savingComposer,
    composerMentionType,
    composerMentionQuery,
    openTurnComposer,
    closeComposer,
    handleComposerTextChange,
    handleSelectMentionRule,
    handleSelectMentionKnowledge,
    removeComposerRule,
    removeComposerKnowledge,
    handleSaveComposerComment,
  } = useInPlaceComposer({
    activeBatchId: defaultBatch?.id || "",
    evaluations,
    setComments,
  });

  const {
    editingCommentId,
    editingText,
    editingRuleIds,
    editingKnowledgeIds,
    savingEdit,
    mentionType: editorMentionType,
    mentionQuery: editorMentionQuery,
    deleteModalCommentId,
    deletingComment,
    startEdit,
    cancelEdit,
    handleEditTextChange,
    handleSelectMentionRule: handleEditorSelectMentionRule,
    handleSelectMentionKnowledge: handleEditorSelectMentionKnowledge,
    handleUpdateComment,
    handleDeleteComment,
    handleCancelDelete,
    handleConfirmDelete,
    removeEditingRule,
    removeEditingKnowledge,
  } = useCommentEditor({
    setComments,
    activeHighlightedCommentId,
    setActiveHighlightedCommentId,
  });

  const handleSelectTextForTurn = (
    messageId: string,
    threadId?: string | null,
    evaluationId?: string | null,
  ) => {
    if (!isOpenBatch || !threadId) {
      if (!isOpenBatch) {
        toast.info(
          "An active collecting feedback batch is required to add comments.",
        );
      }
      return;
    }
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    const text = selection.toString().trim();
    if (!text || text.length < 2) return;

    const elem = document.getElementById(`msg-text-${messageId}`);
    let startOffset = 0;
    let endOffset = text.length;

    if (elem) {
      const fullText = elem.textContent || "";
      const idx = fullText.indexOf(text);
      if (idx !== -1) {
        startOffset = idx;
        endOffset = idx + text.length;
      }
    }

    setInPlaceComposer({
      threadId,
      messageId,
      selectedText: text,
      start: startOffset,
      end: endOffset,
      evaluationId: evaluationId || undefined,
    });
  };

  const handleOpenNoteForTurn = (
    messageId: string,
    threadId?: string | null,
    evaluationId?: string | null,
    initialText?: string,
  ) => {
    if (!isOpenBatch || !threadId) {
      if (!isOpenBatch) {
        toast.info(
          "An active collecting feedback batch is required to add notes.",
        );
      }
      return;
    }
    openTurnComposer(
      messageId,
      evaluationId || undefined,
      threadId,
      initialText,
    );
  };

  const handleSelectRun = (id: string) => {
    setSelectedRunId(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.has("run_id")) {
          next.set("run_id", id);
        } else {
          next.set("test_run_id", id);
        }
        return next;
      },
      { replace: false },
    );
  };

  const handleToggleArchived = (checked: boolean) => {
    setShowArchived(checked);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) {
          next.set("include_archived", "true");
        } else {
          next.delete("include_archived");
          next.delete("archived");
        }
        return next;
      },
      { replace: true },
    );
  };

  const fetchRuns = async () => {
    setLoading(true);
    try {
      const resp = await testRunsListTestRuns({
        limit: 50,
        include_archived: showArchived,
      });
      const data = (resp as any)?.data ?? resp;
      if (data && "runs" in data) {
        const listResp = data as TestRunListResponse;
        setRuns(listResp.runs);
        const resolvedId =
          searchParams.get("test_run_id") ||
          searchParams.get("run_id") ||
          searchParams.get("test-run") ||
          pathRunId ||
          selectedRunId;
        if (listResp.runs.length > 0 && !resolvedId) {
          const firstId = listResp.runs[0].id;
          setSelectedRunId(firstId);
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              if (next.has("run_id")) {
                next.set("run_id", firstId);
              } else {
                next.set("test_run_id", firstId);
              }
              return next;
            },
            { replace: true },
          );
        } else if (resolvedId) {
          setSelectedRunId(resolvedId);
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              const key = next.has("run_id") ? "run_id" : "test_run_id";
              if (next.get(key) !== resolvedId) {
                next.set(key, resolvedId);
                return next;
              }
              return prev;
            },
            { replace: true },
          );
        }
      }
    } catch (err: any) {
      toast.error(err.message, "Failed to load test runs");
    } finally {
      setLoading(false);
    }
  };

  const fetchRunDetail = async (id: string) => {
    try {
      const resp = await testRunsGetTestRun(id);
      const data = (resp as any)?.data ?? resp;
      if (data && "id" in data) {
        setRunDetail(data as TestRunResponse);
      }
    } catch (err: any) {
      toast.error(err.message, "Failed to load test run detail");
    }
  };

  const handleToggleArchive = async (run: TestRunResponse) => {
    setActionLoading(true);
    try {
      if (run.is_archived) {
        await testRunsUnarchiveTestRun(run.id);
        toast.success(
          `Run ${run.id.slice(0, 8)} restored.`,
          "Test run unarchived",
        );
      } else {
        await testRunsArchiveTestRun(run.id);
        toast.info(`Run ${run.id.slice(0, 8)} archived.`);
      }
      await fetchRuns();
      if (selectedRunId === run.id) {
        fetchRunDetail(run.id);
      }
    } catch (err: any) {
      toast.error(err.message, "Failed to toggle test run archive state");
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchRuns();
  }, [activeOrgId, showArchived]);

  useEffect(() => {
    if (targetRunId && targetRunId !== selectedRunId) {
      setSelectedRunId(targetRunId);
    }
  }, [targetRunId]);

  useEffect(() => {
    if (selectedRunId) {
      fetchRunDetail(selectedRunId);
    } else {
      setRunDetail(null);
    }
    setCollapsedCases({});
  }, [selectedRunId]);

  useEffect(() => {
    let mounted = true;
    rulesListRules({ limit: 200 })
      .then((res: any) => {
        if (mounted) setRulesList(extractListItems<RuleResponse>(res));
      })
      .catch(() => {});
    knowledgeListKnowledgeItems({ limit: 200 })
      .then((res: any) => {
        if (mounted)
          setKnowledgeList(extractListItems<KnowledgeItemResponse>(res));
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [activeOrgId]);

  useEffect(() => {
    let mounted = true;
    closeComposer();
    setActiveHighlightedCommentId(null);

    if (!runDetail) {
      setDefaultBatch(null);
      setComments([]);
      setEvaluations([]);
      return;
    }

    const loadBatchesAndComments = async () => {
      try {
        const bRes = await feedbackBatchesListFeedbackBatches({
          limit: 100,
        }).catch(() => ({ data: [] }));
        const bList = extractListItems<BatchResponse>(bRes);
        if (mounted) setAllBatches(bList);

        let resolvedDefaultBatch: BatchResponse | null = null;
        if (runDetail.feedback_batch_id) {
          const matched = bList.find(
            (b) => b.id === runDetail.feedback_batch_id,
          );
          if (matched) {
            resolvedDefaultBatch = matched;
          } else {
            try {
              const singleRes = await feedbackBatchesGetFeedbackBatch(
                runDetail.feedback_batch_id,
              );
              const singleData = (singleRes as any)?.data ?? singleRes;
              if (singleData && "id" in singleData) {
                resolvedDefaultBatch = singleData as BatchResponse;
              }
            } catch {
              // ignore
            }
          }
        }

        // If runDetail has no batch or it's not open, fallback to any open collecting batch
        if (
          !resolvedDefaultBatch ||
          resolvedDefaultBatch.status !== "collecting"
        ) {
          const openBatch = bList.find((b) => b.status === "collecting");
          if (!resolvedDefaultBatch && openBatch) {
            resolvedDefaultBatch = openBatch;
          }
        }

        if (mounted) setDefaultBatch(resolvedDefaultBatch);

        // Fetch comments for this run's batch and/or test case threads
        const commentPromises: Promise<any>[] = [];
        if (runDetail.feedback_batch_id) {
          commentPromises.push(
            commentsListComments({
              batch_id: runDetail.feedback_batch_id,
              limit: 200,
            }).catch(() => ({ data: [] })),
          );
        }
        if (
          resolvedDefaultBatch &&
          resolvedDefaultBatch.id !== runDetail.feedback_batch_id
        ) {
          commentPromises.push(
            commentsListComments({
              batch_id: resolvedDefaultBatch.id,
              limit: 200,
            }).catch(() => ({ data: [] })),
          );
        }

        const threadIds = Array.from(
          new Set(
            (runDetail.case_results || [])
              .map((c) => c.thread_id)
              .filter((id): id is string => Boolean(id)),
          ),
        );

        for (const tid of threadIds) {
          commentPromises.push(
            commentsListComments({ thread_id: tid, limit: 100 }).catch(() => ({
              data: [],
            })),
          );
        }

        const commentResults = await Promise.all(commentPromises);
        const allComms: CommentResponse[] = [];
        for (const cRes of commentResults) {
          allComms.push(...extractListItems<CommentResponse>(cRes));
        }

        // Deduplicate comments by id
        const uniqueComms = Array.from(
          new Map(allComms.map((c) => [c.id, c])).values(),
        );
        if (mounted) setComments(uniqueComms);
        // Fetch evaluations for this run's batch and/or test threads
        const evalPromises: Promise<any>[] = [];
        if (runDetail.feedback_batch_id) {
          evalPromises.push(
            evaluationsListEvaluations({
              batch_id: runDetail.feedback_batch_id,
              include_test: true,
              limit: 200,
            }).catch(() => ({ data: [] })),
          );
        }
        for (const tid of threadIds) {
          evalPromises.push(
            evaluationsListEvaluations({
              thread_id: tid,
              include_test: true,
              limit: 200,
            }).catch(() => ({ data: [] })),
          );
        }

        const evalResults = await Promise.all(evalPromises);
        const allEvals: EvaluationResponse[] = [];
        for (const eRes of evalResults) {
          allEvals.push(...extractListItems<EvaluationResponse>(eRes));
        }
        const uniqueEvals = Array.from(
          new Map(allEvals.map((e) => [e.id, e])).values(),
        );
        if (mounted) setEvaluations(uniqueEvals);
      } catch (err: any) {
        console.error("Failed to load batches/comments for test run", err);
      }
    };

    loadBatchesAndComments();

    return () => {
      mounted = false;
    };
  }, [runDetail?.id, runDetail?.feedback_batch_id, activeOrgId]);

  const handleToggleCase = (caseKey: string) => {
    setCollapsedCases((prev) => ({
      ...prev,
      [caseKey]: !prev[caseKey],
    }));
  };

  const allCasesCollapsed =
    Boolean(runDetail?.case_results && runDetail.case_results.length > 0) &&
    runDetail!.case_results!.every((res, idx) =>
      Boolean(collapsedCases[res.test_case_id || `case-${idx}`]),
    );

  const handleToggleAllCases = () => {
    if (!runDetail?.case_results) return;
    if (allCasesCollapsed) {
      setCollapsedCases({});
    } else {
      const all: Record<string, boolean> = {};
      runDetail.case_results.forEach((res, idx) => {
        all[res.test_case_id || `case-${idx}`] = true;
      });
      setCollapsedCases(all);
    }
  };

  // Polling for running runs
  useEffect(() => {
    if (
      runDetail &&
      (runDetail.status === "running" || runDetail.status === "pending")
    ) {
      const interval = setInterval(() => {
        if (selectedRunId) fetchRunDetail(selectedRunId);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [runDetail?.status, selectedRunId]);

  return (
    <div className="flex-1 flex h-full overflow-hidden bg-stone-50 dark:bg-stone-950">
      {/* Left List Column */}
      <div className="w-80 md:w-96 border-r border-stone-200 dark:border-stone-800 flex flex-col bg-white dark:bg-stone-900 shrink-0">
        <div className="p-4 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h1 className="font-semibold text-stone-900 dark:text-stone-100">
              Test Runs
            </h1>
          </div>
          <button
            onClick={() => {
              fetchRuns();
              if (selectedRunId) fetchRunDetail(selectedRunId);
            }}
            className="p-1.5 text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200 rounded-md hover:bg-stone-100 dark:hover:bg-stone-800"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Filter Toggle */}
        <div className="px-4 py-2 bg-stone-50/70 dark:bg-stone-900/60 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
          <label className="text-xs text-stone-600 dark:text-stone-400 flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => handleToggleArchived(e.target.checked)}
              className="rounded border-stone-300 dark:border-stone-700 text-indigo-600 focus:ring-indigo-500"
            />
            Show archived test runs
          </label>
          <span className="text-[11px] text-stone-400">
            {runs.length} total
          </span>
        </div>

        {/* List Content */}
        <div className="flex-1 overflow-y-auto divide-y divide-stone-100 dark:divide-stone-800/60">
          {loading && runs.length === 0 ? (
            <div className="p-8 text-center text-sm text-stone-500">
              Loading test runs...
            </div>
          ) : runs.length === 0 ? (
            <div className="p-8 text-center text-sm text-stone-500">
              No test runs executed yet. Go to Test Suites to trigger a run.
            </div>
          ) : (
            runs.map((r) => {
              const isSelected = selectedRunId === r.id;
              const isCompleted = r.status === "completed";
              const isFailed = r.status === "failed";
              const isPending =
                r.status === "pending" || r.status === "running";

              return (
                <button
                  key={r.id}
                  onClick={() => handleSelectRun(r.id)}
                  className={`w-full text-left p-4 transition-colors flex items-start justify-between gap-3 ${
                    isSelected
                      ? "bg-indigo-50/60 dark:bg-indigo-950/20 border-l-2 border-indigo-600 dark:border-indigo-400"
                      : "hover:bg-stone-50 dark:hover:bg-stone-800/40"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-stone-900 dark:text-stone-100 truncate">
                        {r.suite_name || "Test Suite"}
                      </span>
                      {r.is_archived && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 font-medium">
                          Archived
                        </span>
                      )}
                      {isCompleted && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 font-medium">
                          Done
                        </span>
                      )}
                      {isPending && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400 font-medium animate-pulse">
                          Running
                        </span>
                      )}
                      {isFailed && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-400 font-medium">
                          Failed
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 dark:text-stone-400 mt-1 truncate">
                      Baseline: {r.snapshot_name || "Snapshot"}
                    </div>
                    <div className="flex items-center justify-between mt-2 text-[11px] text-stone-400">
                      <div className="flex items-center gap-2">
                        <span>
                          {r.completed_cases}/{r.total_cases} cases
                        </span>
                        <span>•</span>
                        <TimeAgo date={r.created_at} />
                      </div>
                    </div>
                  </div>
                  <ChevronRight
                    className={`w-4 h-4 mt-1 text-stone-400 ${
                      isSelected ? "text-indigo-600 dark:text-indigo-400" : ""
                    }`}
                  />
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Right Detail Pane */}
      <div className="flex-1 flex flex-col h-full overflow-y-auto">
        {runDetail ? (
          <div className="p-6 max-w-7xl space-y-6">
            <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">
                      Run {runDetail.id.slice(0, 8)}
                    </h2>
                    {runDetail.is_archived && (
                      <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 border border-stone-200 dark:border-stone-700">
                        ARCHIVED
                      </span>
                    )}
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                        runDetail.status === "completed"
                          ? "bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400"
                          : runDetail.status === "running"
                            ? "bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400 animate-pulse"
                            : "bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-400"
                      }`}
                    >
                      {runDetail.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-stone-500">
                    <span>
                      Suite: <strong>{runDetail.suite_name}</strong>
                    </span>
                    <span>•</span>
                    <span>
                      Baseline Snapshot:{" "}
                      {runDetail.environment_snapshot_id ? (
                        <Link
                          to={`/snapshots?snapshot_id=${runDetail.environment_snapshot_id}`}
                          className="font-bold text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-1"
                        >
                          <span>{runDetail.snapshot_name || "Snapshot"}</span>
                        </Link>
                      ) : (
                        <strong>{runDetail.snapshot_name || "Snapshot"}</strong>
                      )}
                    </span>
                    {runDetail.temporal_workflow_id && (
                      <>
                        <span>•</span>
                        <a
                          href={`https://temporal.silvestre.cr/namespaces/default/workflows/${encodeURIComponent(runDetail.temporal_workflow_id)}${runDetail.temporal_run_id ? `/${encodeURIComponent(runDetail.temporal_run_id)}` : ""}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-stone-500 hover:text-stone-800 dark:hover:text-stone-300 hover:underline inline-flex items-center gap-1"
                          title="Open Test Run workflow in Temporal Web UI"
                        >
                          <span>Temporal Run</span>
                        </a>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {defaultBatch ? (
                    <Link
                      to={`/batches/${defaultBatch.id}`}
                      className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline transition"
                      title={
                        defaultBatch.status === "collecting"
                          ? `Default Feedback Batch: ${defaultBatch.title} (Collecting - comments enabled)`
                          : `Default Feedback Batch: ${defaultBatch.title} (${defaultBatch.status})`
                      }
                    >
                      <Layers className="w-3.5 h-3.5" />
                      <span>
                        {defaultBatch.status === "collecting"
                          ? `Batch: ${defaultBatch.title}`
                          : `Batch: ${defaultBatch.title} (${defaultBatch.status})`}
                      </span>
                    </Link>
                  ) : runDetail.feedback_batch_id ? (
                    <Link
                      to={`/batches/${runDetail.feedback_batch_id}`}
                      className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline transition"
                    >
                      <Layers className="w-3.5 h-3.5" />
                      <span>Open Batch</span>
                    </Link>
                  ) : null}

                  <div className="relative">
                    <button
                      onClick={() => setMenuOpen(!menuOpen)}
                      className="p-1.5 text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 border border-stone-200 dark:border-stone-800 transition"
                      title="More actions"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {menuOpen && (
                      <div
                        className="absolute right-0 mt-1 w-44 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg shadow-lg py-1 z-10"
                        onMouseLeave={() => setMenuOpen(false)}
                      >
                        <button
                          onClick={() => {
                            setMenuOpen(false);
                            handleToggleArchive(runDetail);
                          }}
                          disabled={actionLoading}
                          className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-50"
                        >
                          {runDetail.is_archived ? (
                            <>
                              <ArchiveRestore className="w-3.5 h-3.5 text-stone-500" />
                              Restore Run
                            </>
                          ) : (
                            <>
                              <Archive className="w-3.5 h-3.5 text-stone-500" />
                              Archive Run
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-6 space-y-1.5">
                <div className="flex items-center justify-between text-xs text-stone-500">
                  <span>Execution Progress</span>
                  <span>
                    {runDetail.completed_cases} of {runDetail.total_cases} cases
                    completed
                  </span>
                </div>
                <div className="w-full bg-stone-100 dark:bg-stone-800 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        runDetail.total_cases > 0
                          ? (runDetail.completed_cases /
                              runDetail.total_cases) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>

              {/* Real Evaluations Score Distribution Charts */}
              {evaluations.length > 0 &&
                (() => {
                  const totalEvals = evaluations.length;
                  const calcCategoryData = (
                    getter: (
                      e: EvaluationResponse,
                    ) => number | null | undefined,
                  ) => {
                    const values = evaluations
                      .map(getter)
                      .filter(
                        (v): v is number => typeof v === "number" && !isNaN(v),
                      );
                    if (values.length === 0) return null;
                    const avg =
                      values.reduce((a, b) => a + b, 0) / values.length;
                    const dist: Record<number, number> = {
                      1: 0,
                      2: 0,
                      3: 0,
                      4: 0,
                      5: 0,
                    };
                    for (const v of values) {
                      const bucket = Math.max(1, Math.min(5, Math.round(v)));
                      dist[bucket] = (dist[bucket] || 0) + 1;
                    }
                    return { avg, dist, count: values.length };
                  };

                  const catDefs = [
                    {
                      key: "composite",
                      label: "Overall Score",
                      data: calcCategoryData((e) => e.score),
                    },
                    {
                      key: "accuracy",
                      label: "Accuracy",
                      data: calcCategoryData((e) => e.accuracy_score),
                    },
                    {
                      key: "instruction_adherence",
                      label: "Instruction Adherence",
                      data: calcCategoryData(
                        (e) => e.instruction_adherence_score,
                      ),
                    },
                    {
                      key: "tone",
                      label: "Tone & Courtesy",
                      data: calcCategoryData((e) => e.tone_score),
                    },
                    {
                      key: "tool_efficiency",
                      label: "Tool Efficiency",
                      data: calcCategoryData((e) => e.tool_efficiency_score),
                    },
                  ].filter((c) => c.data !== null);

                  if (catDefs.length === 0) return null;

                  return (
                    <div className="mt-6 pt-6 border-t border-stone-100 dark:border-stone-800 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                          <h3 className="text-xs font-semibold text-stone-700 dark:text-stone-300 uppercase tracking-wider">
                            Score Distributions (1 - 5 Ratings)
                          </h3>
                        </div>
                        <span className="text-[11px] text-stone-400 font-mono">
                          {totalEvals}{" "}
                          {totalEvals === 1 ? "evaluation" : "evaluations"}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                        {catDefs.map(({ key, label, data }) => {
                          if (!data) return null;
                          const counts = [1, 2, 3, 4, 5].map(
                            (val) => data.dist[val] || 0,
                          );
                          const maxCount = Math.max(...counts, 1);

                          return (
                            <div
                              key={key}
                              className="p-3.5 bg-stone-50 dark:bg-stone-950 rounded-xl border border-stone-200 dark:border-stone-800/80 flex flex-col justify-between"
                            >
                              <div>
                                <div className="flex items-baseline justify-between gap-1">
                                  <span
                                    className="text-xs font-semibold text-stone-800 dark:text-stone-200 truncate"
                                    title={label}
                                  >
                                    {label}
                                  </span>
                                  <span className="text-xs font-bold font-mono text-indigo-600 dark:text-indigo-400 shrink-0">
                                    {data.avg.toFixed(2)}
                                  </span>
                                </div>
                                <div className="text-[10px] text-stone-400 mt-0.5">
                                  {data.count} rated
                                </div>
                              </div>

                              <div className="space-y-1.5 mt-3">
                                {[1, 2, 3, 4, 5].map((rating) => {
                                  const count = data.dist[rating] || 0;
                                  const pct = (count / maxCount) * 100;
                                  return (
                                    <div
                                      key={rating}
                                      className="flex items-center gap-1.5 text-xs"
                                    >
                                      <span className="w-2.5 font-mono text-[10px] text-stone-400">
                                        {rating}
                                      </span>
                                      <div className="flex-1 bg-stone-200/70 dark:bg-stone-800/80 rounded h-1.5 overflow-hidden">
                                        <div
                                          className="bg-indigo-600 dark:bg-indigo-500 h-1.5 rounded transition-all duration-300"
                                          style={{ width: `${pct}%` }}
                                        />
                                      </div>
                                      <span className="w-4 text-right font-mono text-[10px] text-stone-500 dark:text-stone-400">
                                        {count}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
            </div>

            {/* Test Case Results Section */}
            {runDetail.case_results && runDetail.case_results.length > 0 && (
              <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-stone-100 dark:border-stone-800">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm text-stone-900 dark:text-stone-100">
                      Test Case Results ({runDetail.case_results.length})
                    </h3>
                    <span className="text-xs text-stone-500 hidden sm:inline">
                      • Expected vs. Actual Generated Output
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleAllCases}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-md transition"
                  >
                    {allCasesCollapsed ? (
                      <>
                        <ChevronDown className="w-3.5 h-3.5" />
                        <span>Expand All</span>
                      </>
                    ) : (
                      <>
                        <ChevronRight className="w-3.5 h-3.5" />
                        <span>Collapse All</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="space-y-4">
                  {runDetail.case_results.map(
                    (res: TestCaseResult, idx: number) => {
                      const caseKey = res.test_case_id || `case-${idx}`;
                      const isCollapsed = Boolean(collapsedCases[caseKey]);

                      const turns: TestCaseTurnComparison[] =
                        res.turns && res.turns.length > 0
                          ? res.turns
                          : res.input_prompt ||
                              res.expected_output ||
                              res.actual_output
                            ? [
                                {
                                  turn_index: 0,
                                  user_message:
                                    res.input_prompt || "(No prompt)",
                                  expected_agent_message:
                                    res.expected_output || null,
                                  actual_agent_message:
                                    res.actual_output || null,
                                },
                              ]
                            : [];

                      return (
                        <div
                          key={caseKey}
                          className="rounded-xl border border-stone-200 dark:border-stone-800 overflow-hidden bg-white dark:bg-stone-900 shadow-xs transition-shadow"
                        >
                          {/* Case Header (Clickable Accordion Trigger) */}
                          <div
                            role="button"
                            tabIndex={0}
                            aria-expanded={!isCollapsed}
                            onClick={() => handleToggleCase(caseKey)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleToggleCase(caseKey);
                              }
                            }}
                            className={`p-4 bg-stone-50 dark:bg-stone-900/90 cursor-pointer select-none hover:bg-stone-100/80 dark:hover:bg-stone-800/60 transition-colors flex flex-wrap items-center justify-between gap-3 ${
                              !isCollapsed
                                ? "border-b border-stone-200 dark:border-stone-800"
                                : ""
                            }`}
                          >
                            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                              <span className="text-stone-400 dark:text-stone-500 shrink-0">
                                {isCollapsed ? (
                                  <ChevronRight className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </span>
                              <span className="w-6 h-6 rounded-full bg-stone-200 dark:bg-stone-800 text-stone-700 dark:text-stone-300 flex items-center justify-center text-xs font-mono font-bold shrink-0">
                                {idx + 1}
                              </span>
                              <span className="font-semibold text-sm text-stone-900 dark:text-stone-100 truncate">
                                {res.title}
                              </span>
                              <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-stone-200/60 dark:bg-stone-800 text-stone-600 dark:text-stone-400 shrink-0">
                                {turns.length}{" "}
                                {turns.length === 1 ? "turn" : "turns"}
                              </span>
                            </div>

                            {/* Case Actions / Links */}
                            <div
                              className="flex flex-wrap items-center gap-3"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              {res.thread_id && (
                                <Link
                                  to={`/threads/${res.thread_id}`}
                                  className="inline-flex items-center gap-1 text-[11px] font-mono text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:underline transition"
                                  title="Open Conversation Thread"
                                >
                                  <MessageSquare className="w-3 h-3" />
                                  <span>
                                    Thread: {res.thread_id.slice(0, 8)}...
                                  </span>
                                </Link>
                              )}

                              {res.temporal_workflow_id && (
                                <a
                                  href={`https://temporal.silvestre.cr/namespaces/default/workflows/${encodeURIComponent(res.temporal_workflow_id)}${res.temporal_run_id ? `/${encodeURIComponent(res.temporal_run_id)}` : ""}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] font-mono text-sky-600 dark:text-sky-400 hover:underline transition"
                                  title="Open Test Case in Temporal Web UI"
                                >
                                  <span>Temporal Case</span>
                                </a>
                              )}
                            </div>
                          </div>

                          {/* Expanded Content */}
                          {!isCollapsed && (
                            <>
                              {/* Conversational Turns Flow */}
                              <div className="p-4 space-y-6 bg-stone-50/30 dark:bg-stone-950/30">
                                {turns.length === 0 ? (
                                  <div className="text-center py-6 text-xs text-stone-400">
                                    No conversation turns recorded for this test
                                    case.
                                  </div>
                                ) : (
                                  turns.map((turn, tIdx) => {
                                    const turnMsgId =
                                      turn.message_id ||
                                      `turn-${caseKey}-${tIdx}`;
                                    const turnEval = evaluations.find(
                                      (e) =>
                                        (turn.message_id &&
                                          e.message_id === turn.message_id) ||
                                        (turn.agent_run_id &&
                                          e.run_id === turn.agent_run_id),
                                    );
                                    const isComposerForThisTurn =
                                      inPlaceComposer !== null &&
                                      ((turn.message_id &&
                                        inPlaceComposer.messageId ===
                                          turn.message_id) ||
                                        (!turn.message_id &&
                                          inPlaceComposer.threadId ===
                                            res.thread_id &&
                                          inPlaceComposer.messageId ===
                                            turnMsgId));

                                    const turnComments = comments
                                      .filter((c) => {
                                        if (
                                          turn.message_id &&
                                          c.message_id === turn.message_id
                                        )
                                          return true;
                                        if (
                                          !c.message_id &&
                                          c.thread_id === res.thread_id &&
                                          turns.length === 1
                                        )
                                          return true;
                                        return false;
                                      })
                                      .sort((a, b) => {
                                        const aStart = a.selection_start ?? 0;
                                        const bStart = b.selection_start ?? 0;
                                        if (aStart !== bStart)
                                          return aStart - bStart;
                                        return (
                                          new Date(a.created_at).getTime() -
                                          new Date(b.created_at).getTime()
                                        );
                                      });

                                    return (
                                      <div
                                        key={tIdx}
                                        className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start pb-6 border-b border-stone-100 dark:border-stone-800/80 last:border-b-0 last:pb-0"
                                      >
                                        {/* Left: 8 cols for turn execution, comparison, and evaluation */}
                                        <div className="lg:col-span-8 space-y-3">
                                          {/* 1. User Message Row */}
                                          <div className="flex items-start gap-2.5">
                                            <div className="w-6 h-6 rounded-full bg-stone-200 dark:bg-stone-700 text-stone-700 dark:text-stone-200 flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                                              <User className="w-3.5 h-3.5" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2 mb-1">
                                                <span className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider">
                                                  User Message
                                                </span>
                                                <span className="text-[10px] text-stone-400 font-mono">
                                                  Turn {turn.turn_index}
                                                </span>
                                              </div>
                                              <div className="p-3 rounded-lg bg-stone-100 dark:bg-stone-800/80 border border-stone-200 dark:border-stone-700/60 text-xs text-stone-900 dark:text-stone-100 whitespace-pre-wrap font-sans leading-relaxed">
                                                {turn.user_message || (
                                                  <span className="text-stone-400 italic">
                                                    (Empty prompt)
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </div>

                                          {/* 2. Side-by-Side Agent Comparison Row */}
                                          <div className="flex items-start gap-2.5">
                                            <div className="w-6 h-6 rounded-full bg-stone-200 dark:bg-stone-700 text-stone-700 dark:text-stone-200 flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                                              <Bot className="w-3.5 h-3.5" />
                                            </div>
                                            <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 gap-3">
                                              {/* Expected */}
                                              <div className="space-y-1.5 flex flex-col">
                                                <div className="flex items-center justify-between h-5">
                                                  <span className="text-[10px] font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wider">
                                                    Expected
                                                  </span>
                                                </div>
                                                <div className="flex-1 p-3 bg-white dark:bg-stone-900 rounded-lg border border-stone-200 dark:border-stone-800 text-xs text-stone-700 dark:text-stone-300 whitespace-pre-wrap font-sans leading-relaxed">
                                                  {turn.expected_agent_message ? (
                                                    turn.expected_agent_message
                                                  ) : (
                                                    <span className="text-stone-400 italic">
                                                      No expected response
                                                      specified
                                                    </span>
                                                  )}
                                                </div>
                                              </div>

                                              {/* Generated */}
                                              <div className="space-y-1.5 flex flex-col">
                                                <div className="flex items-center justify-between h-5">
                                                  <span className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                                                    Generated
                                                  </span>
                                                  <div className="flex items-center gap-2">
                                                    {isOpenBatch &&
                                                      res.thread_id && (
                                                        <button
                                                          type="button"
                                                          data-add-note-btn
                                                          onClick={() => {
                                                            const prefilled = `Generated message:\n${turn.actual_agent_message || ""}\n\nExpected message:\n${turn.expected_agent_message || ""}`;
                                                            handleOpenNoteForTurn(
                                                              turnMsgId,
                                                              res.thread_id,
                                                              turnEval?.id,
                                                              prefilled,
                                                            );
                                                          }}
                                                          className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 transition cursor-pointer hover:underline"
                                                          title="Add note to feedback batch with pre-populated message comparison"
                                                        >
                                                          <Plus className="w-3 h-3" />
                                                          <span>Add note</span>
                                                        </button>
                                                      )}
                                                    {turn.agent_run_id ? (
                                                      <Link
                                                        to={`/runs/${turn.agent_run_id}`}
                                                        className="inline-flex items-center gap-1 text-[11px] font-mono text-indigo-600 dark:text-indigo-400 hover:underline transition"
                                                        title="Inspect execution trace, tool calls, and tokens for this run"
                                                      >
                                                        <Activity className="w-3 h-3" />
                                                        <span>
                                                          Run:{" "}
                                                          {turn.agent_run_id.slice(
                                                            0,
                                                            8,
                                                          )}
                                                          ...
                                                        </span>
                                                      </Link>
                                                    ) : (
                                                      <span className="text-[10px] text-stone-400 italic">
                                                        No run trace linked
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                                <div className="flex-1 p-3 bg-indigo-50/30 dark:bg-indigo-950/20 rounded-lg border border-indigo-100 dark:border-indigo-900/40 text-xs text-stone-900 dark:text-stone-100 font-sans leading-relaxed">
                                                  {turn.actual_agent_message ? (
                                                    <HighlightedMessageText
                                                      msgId={turnMsgId}
                                                      content={
                                                        turn.actual_agent_message
                                                      }
                                                      comments={turnComments}
                                                      activeHighlightedCommentId={
                                                        activeHighlightedCommentId
                                                      }
                                                      activeSelection={
                                                        isComposerForThisTurn &&
                                                        inPlaceComposer?.selectedText
                                                          ? {
                                                              text: inPlaceComposer.selectedText,
                                                              start:
                                                                inPlaceComposer.start,
                                                              end: inPlaceComposer.end,
                                                            }
                                                          : null
                                                      }
                                                      onSelectText={
                                                        isOpenBatch &&
                                                        res.thread_id
                                                          ? () =>
                                                              handleSelectTextForTurn(
                                                                turnMsgId,
                                                                res.thread_id,
                                                              )
                                                          : undefined
                                                      }
                                                      onCommentClick={(cid) => {
                                                        setActiveHighlightedCommentId(
                                                          cid,
                                                        );
                                                        const card =
                                                          document.getElementById(
                                                            `comment-card-${cid}`,
                                                          );
                                                        card?.scrollIntoView({
                                                          behavior: "smooth",
                                                          block: "nearest",
                                                        });
                                                      }}
                                                    />
                                                  ) : (
                                                    <span className="text-stone-400 italic">
                                                      No agent response
                                                      generated
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>

                                          {/* 3. Turn Evaluation Feedback (if present) */}
                                          {turnEval && (
                                            <div className="ml-8 p-3 bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/80 dark:border-amber-800/60 rounded-lg text-xs space-y-1.5">
                                              <div className="flex flex-wrap items-center justify-between gap-2">
                                                <span className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                                                  Evaluator Feedback
                                                  {typeof turnEval.score ===
                                                    "number" && (
                                                    <span className="font-mono px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-300 text-[10px]">
                                                      {turnEval.score.toFixed(
                                                        1,
                                                      )}{" "}
                                                      / 5
                                                    </span>
                                                  )}
                                                </span>
                                                <div className="flex items-center gap-2 text-[10px] text-amber-800/80 dark:text-amber-300/80">
                                                  {typeof turnEval.accuracy_score ===
                                                    "number" && (
                                                    <span>
                                                      Accuracy:{" "}
                                                      {turnEval.accuracy_score.toFixed(
                                                        1,
                                                      )}{" "}
                                                      / 5
                                                    </span>
                                                  )}
                                                  {typeof turnEval.instruction_adherence_score ===
                                                    "number" && (
                                                    <span>
                                                      • Adherence:{" "}
                                                      {turnEval.instruction_adherence_score.toFixed(
                                                        1,
                                                      )}{" "}
                                                      / 5
                                                    </span>
                                                  )}
                                                  {typeof turnEval.tone_score ===
                                                    "number" && (
                                                    <span>
                                                      • Tone:{" "}
                                                      {turnEval.tone_score.toFixed(
                                                        1,
                                                      )}{" "}
                                                      / 5
                                                    </span>
                                                  )}
                                                  {typeof turnEval.tool_efficiency_score ===
                                                    "number" && (
                                                    <span>
                                                      • Tools:{" "}
                                                      {turnEval.tool_efficiency_score.toFixed(
                                                        1,
                                                      )}{" "}
                                                      / 5
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              {turnEval.reasoning && (
                                                <div className="text-amber-900/90 dark:text-amber-200/90 leading-relaxed font-sans whitespace-pre-wrap">
                                                  <EntityLinkedText
                                                    text={turnEval.reasoning}
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>

                                        {/* Right: 4 cols for turn comments and in-place composer */}
                                        <div className="lg:col-span-4 space-y-2.5 pt-4">
                                          {turnComments.length > 0 && (
                                            <div className="space-y-2">
                                              <div className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider">
                                                Feedback & Notes (
                                                {turnComments.length})
                                              </div>
                                              {turnComments.map((comment) => (
                                                <ThreadCommentCard
                                                  key={comment.id}
                                                  comment={comment}
                                                  rulesList={rulesList}
                                                  knowledgeList={knowledgeList}
                                                  batch={
                                                    defaultBatch || undefined
                                                  }
                                                  isHighlighted={
                                                    activeHighlightedCommentId ===
                                                    comment.id
                                                  }
                                                  isEditing={
                                                    editingCommentId ===
                                                    comment.id
                                                  }
                                                  editingText={editingText}
                                                  editingRuleIds={
                                                    editingRuleIds
                                                  }
                                                  editingKnowledgeIds={
                                                    editingKnowledgeIds
                                                  }
                                                  savingEdit={savingEdit}
                                                  mentionType={
                                                    editorMentionType
                                                  }
                                                  mentionQuery={
                                                    editorMentionQuery
                                                  }
                                                  mentionTarget="editor"
                                                  onCardClick={(c) =>
                                                    setActiveHighlightedCommentId(
                                                      c.id,
                                                    )
                                                  }
                                                  onStartEdit={startEdit}
                                                  onCancelEdit={cancelEdit}
                                                  onSaveEdit={
                                                    handleUpdateComment
                                                  }
                                                  onDeleteComment={
                                                    handleDeleteComment
                                                  }
                                                  onEditTextChange={
                                                    handleEditTextChange
                                                  }
                                                  onSelectMentionRule={
                                                    handleEditorSelectMentionRule
                                                  }
                                                  onSelectMentionKnowledge={
                                                    handleEditorSelectMentionKnowledge
                                                  }
                                                  onRemoveEditingRule={
                                                    removeEditingRule
                                                  }
                                                  onRemoveEditingKnowledge={
                                                    removeEditingKnowledge
                                                  }
                                                />
                                              ))}
                                            </div>
                                          )}

                                          {isComposerForThisTurn && (
                                            <InPlaceCommentComposer
                                              composer={inPlaceComposer}
                                              composerText={composerText}
                                              composerRuleIds={composerRuleIds}
                                              composerKnowledgeIds={
                                                composerKnowledgeIds
                                              }
                                              rulesList={rulesList}
                                              knowledgeList={knowledgeList}
                                              activeBatch={
                                                defaultBatch || undefined
                                              }
                                              savingComposer={savingComposer}
                                              mentionType={composerMentionType}
                                              mentionQuery={
                                                composerMentionQuery
                                              }
                                              mentionTarget="composer"
                                              onClose={closeComposer}
                                              onTextChange={
                                                handleComposerTextChange
                                              }
                                              onSave={handleSaveComposerComment}
                                              onSelectMentionRule={
                                                handleSelectMentionRule
                                              }
                                              onSelectMentionKnowledge={
                                                handleSelectMentionKnowledge
                                              }
                                              onRemoveRule={removeComposerRule}
                                              onRemoveKnowledge={
                                                removeComposerKnowledge
                                              }
                                            />
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>

                              {/* Bottom Collapse Footer */}
                              <div className="px-4 py-2.5 bg-stone-50/80 dark:bg-stone-900/80 border-t border-stone-200 dark:border-stone-800 flex items-center justify-between text-xs">
                                <span className="text-[11px] text-stone-400">
                                  Test case {idx + 1} of{" "}
                                  {runDetail.case_results?.length}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleToggleCase(caseKey)}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 bg-white dark:bg-stone-800 hover:bg-stone-100 dark:hover:bg-stone-700 border border-stone-200 dark:border-stone-700 rounded-md transition"
                                >
                                  <ChevronUp className="w-3.5 h-3.5" />
                                  <span>Collapse case</span>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            )}

            {/* Quick Actions Card */}
            <div className="bg-stone-100 dark:bg-stone-900/60 rounded-xl p-4 border border-stone-200 dark:border-stone-800 flex items-center justify-between">
              <div className="text-xs text-stone-600 dark:text-stone-400">
                Want to compare this execution against a baseline or candidate
                prompt snapshot?
              </div>
              <Link
                to={`/test-runs/compare?run_a_id=${runDetail.id}`}
                className="px-3 py-1.5 bg-stone-900 dark:bg-stone-100 hover:bg-stone-800 dark:hover:bg-stone-200 text-white dark:text-stone-900 rounded-lg text-xs font-medium flex items-center gap-1.5 transition"
              >
                <TrendingUp className="w-3.5 h-3.5" />
                Compare with Another Run
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-stone-400">
            Select a test run to inspect execution progress.
          </div>
        )}
      </div>

      {/* Delete Comment Confirmation Modal */}
      {deleteModalCommentId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl max-w-sm w-full p-5 shadow-xl space-y-4">
            <h3 className="text-sm font-semibold text-stone-900 dark:text-stone-100">
              Delete Comment
            </h3>
            <p className="text-xs text-stone-600 dark:text-stone-400">
              Are you sure you want to delete this comment from the feedback
              batch?
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleCancelDelete}
                className="px-3 py-1.5 text-xs text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 font-medium rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deletingComment}
                className="px-3 py-1.5 text-xs bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg transition disabled:opacity-50"
              >
                {deletingComment ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
